import React, { useState, useEffect, useCallback } from 'react';
import CreateEventModal from './CreateEventModal';
import ContactPicker from './common/ContactPicker';
import ProposeAppointmentModal from './appointments/ProposeAppointmentModal';
import PendingRequestsPanel from './appointments/PendingRequestsPanel';
import {
  Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight, Clock, MapPin, Users, FileText, Video,
  Sparkles, Brain, AlertTriangle, Target, Coffee, Zap, TrendingUp, Bell, Briefcase, Scale,
  ChevronDown, ChevronUp, Play, Sun, Moon, Sunrise, Trash2, Edit3, Mail, UserPlus, X, Settings,
  Eye, Pencil, PlusCircle, Check, Share2, Phone
} from 'lucide-react';
import { useDataCache } from '../context/DataCacheContext';
import api, { getApiBaseUrl } from '../services/api';

// Helper to construct full API URL for fetch calls
const getApiUrl = (path) => {
  const baseUrl = getApiBaseUrl();
  // Remove trailing slash from base and leading slash from path to avoid double slashes
  const cleanBase = baseUrl.replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : '/' + path;
  return cleanBase + cleanPath;
};


// ─── Category Color Map ─────────────────────────────────────
const CATEGORY_COLORS = {
  consultation: '#14b8a6',  // teal
  court: '#ef4444',         // red
  meeting: '#3b82f6',       // blue
  deadline: '#f59e0b',      // amber
  deposition: '#8b5cf6',    // violet
  personal: '#22c55e',      // green
  focus: '#06b6d4',         // cyan
  other: '#6366f1',         // indigo
};

const getEventColorStatic = (event) => {
  const cat = event?.category || event?.event_type || 'other';
  return CATEGORY_COLORS[cat] || CATEGORY_COLORS['other'];
};

const Calendar = () => {
  const { getCache, setCache } = useDataCache();
  const [currentDate, setCurrentDate] = useState(new Date());
  // Initialize from cache
  const [events, setEvents] = useState(() => {
    const cached = getCache('calendar_events');
    return cached?.data || [];
  });
  const [view, setView] = useState('month');
  // B4: computed_deadlines + litigation_events — the calendar's second source,
  // shared with the dashboard's Today's Events card so the two agree.
  const [computedItems, setComputedItems] = useState({ deadlines: [], litigation_events: [] });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(() => {
    const cached = getCache('calendar_events');
    return !cached?.data;
  });
  const [aiBriefing, setAiBriefing] = useState(null);
  const [caseDeadlines, setCaseDeadlines] = useState(() => {
    const cached = getCache('calendar_deadlines');
    return cached?.data || [];
  });
  const [showBriefing, setShowBriefing] = useState(true);
  const [conflicts, setConflicts] = useState([]);
  const [focusSuggestions, setFocusSuggestions] = useState([]);
  const [calendarUsers, setCalendarUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null); // null = current user
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showDelegationModal, setShowDelegationModal] = useState(false);
  const [myDelegates, setMyDelegates] = useState([]);
  const [delegatedToMe, setDelegatedToMe] = useState([]);
  const [delegationLoading, setDelegationLoading] = useState(false);
  const [showFocusTimeModal, setShowFocusTimeModal] = useState(false);
  const [proposeApptOpen, setProposeApptOpen] = useState(false);
  const [proposeReload, setProposeReload] = useState(0);
  const [newEventDate, setNewEventDate] = useState(null);
  const [pendingAppointments, setPendingAppointments] = useState([]);
  const [showPendingPanel, setShowPendingPanel] = useState(true);
  const [approving, setApproving] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  // Legal Intel
  const [showLegalIntel, setShowLegalIntel] = useState(false);
  const [legalIntelTab, setLegalIntelTab] = useState('health');
  // Demoted off the calendar 07-22 — starving in prod (4/5 tabs empty; only
  // calendar_time_suggestions populated). Panel + fetch code retained; flip to resurface.
  const LEGAL_INTEL_ON_CALENDAR = false;
  const [legalHealth, setLegalHealth] = useState(null);
  const [legalChains, setLegalChains] = useState([]);
  const [legalDarkPeriods, setLegalDarkPeriods] = useState([]);
  const [legalTimeSuggestions, setLegalTimeSuggestions] = useState([]);
  const [legalComms, setLegalComms] = useState([]);
  const [legalLoading, setLegalLoading] = useState(false);
  const [firmColors, setFirmColors] = useState(null);

  const getEventColor = useCallback((event) => {
    const cat = event?.category || event?.event_type || 'other';
    const merged = firmColors ? { ...CATEGORY_COLORS, ...firmColors } : CATEGORY_COLORS;
    return merged[cat] || merged['other'] || CATEGORY_COLORS['other'];
  }, [firmColors]);


  const fetchCalendarUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/calendar/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCalendarUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch calendar users:', error);
    }
  };

  const fetchDelegations = async () => {
    try {
      const token = localStorage.getItem('token');
      const [delegatesRes, delegatedRes] = await Promise.all([
        fetch('/api/calendar/delegates', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/calendar/delegated-to-me', { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (delegatesRes.ok) {
        setMyDelegates(await delegatesRes.json());
      }
      if (delegatedRes.ok) {
        setDelegatedToMe(await delegatedRes.json());
      }
    } catch (error) {
      console.error('Failed to fetch delegations:', error);
    }
  };


  const fetchLegalIntelData = useCallback(async (tab) => {
    setLegalLoading(true);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    try {
      if (tab === 'health') {
        const r = await fetch(getApiUrl('/api/calendar-health/'), { headers });
        if (r.ok) setLegalHealth(await r.json());
      } else if (tab === 'chains') {
        const r = await fetch(getApiUrl('/api/deadline-chains?status=pending_review'), { headers });
        if (r.ok) setLegalChains(await r.json());
      } else if (tab === 'dark') {
        const r = await fetch(getApiUrl('/api/calendar/unavailable-periods'), { headers });
        if (r.ok) setLegalDarkPeriods(await r.json());
      } else if (tab === 'time') {
        const r = await fetch(getApiUrl('/api/time-suggestions/'), { headers });
        if (r.ok) setLegalTimeSuggestions(await r.json());
      } else if (tab === 'comms') {
        const r = await fetch(getApiUrl('/api/client-communications/pending'), { headers });
        if (r.ok) setLegalComms(await r.json());
      }
    } catch (e) { console.error('Legal intel fetch failed:', e); }
    finally { setLegalLoading(false); }
  }, []);

  useEffect(() => {
    if (showLegalIntel) fetchLegalIntelData(legalIntelTab);
  }, [showLegalIntel, legalIntelTab, fetchLegalIntelData]);

  const handleChainAction = async (id, action) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(getApiUrl(`/api/deadline-chains/${id}/${action}`), { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } });
      setLegalChains(prev => prev.filter(c => c.id !== id));
    } catch (e) { console.error('Chain action failed:', e); }
  };

  const handleDeletePeriod = async (id) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(getApiUrl(`/api/calendar/unavailable-periods/${id}`), { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      setLegalDarkPeriods(prev => prev.filter(p => p.id !== id));
    } catch (e) { console.error('Delete period failed:', e); }
  };

  const handleCommAction = async (id, action) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(getApiUrl(`/api/client-communications/${id}/${action}`), { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } });
      setLegalComms(prev => prev.filter(c => c.id !== id));
    } catch (e) { console.error('Comm action failed:', e); }
  };

  const fetchPendingAppointments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('api/client-portal/staff/appointments/pending'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingAppointments(data.appointments || []);
      }
    } catch (error) {
      console.error('Failed to fetch pending appointments:', error);
    }
  };

  const handleApproveAppointment = async (id) => {
    setApproving(id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`api/client-portal/staff/appointments/${id}/approve`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        fetchPendingAppointments();
        fetchEvents();
      }
    } catch (error) {
      console.error('Failed to approve appointment:', error);
    } finally {
      setApproving(null);
    }
  };

  // offerAlternatives decides whether declining ALSO queues an outbound call to
  // the client with fresh times. It used to happen on every decline, silently.
  // Defaults false here and server-side, so nothing calls a client by omission.
  const handleRejectAppointment = async (id, reason, offerAlternatives = false) => {
    setApproving(id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`api/client-portal/staff/appointments/${id}/reject`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason || '', offer_alternatives: !!offerAlternatives })
      });
      if (response.ok) {
        fetchPendingAppointments();
        setRejectingId(null);
        setRejectReason('');
      }
    } catch (error) {
      console.error('Failed to reject appointment:', error);
    } finally {
      setApproving(null);
    }
  };

  const addDelegate = async (delegateId, permissions) => {
    setDelegationLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        delegate_id: delegateId,
        can_view: permissions.can_view || true,
        can_edit: permissions.can_edit || false,
        can_create: permissions.can_create || false,
        can_delete: permissions.can_delete || false
      });

      const response = await fetch(getApiUrl(`/api/calendar/delegates?${params}`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchDelegations();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to add delegate');
      }
    } catch (error) {
      console.error('Failed to add delegate:', error);
    } finally {
      setDelegationLoading(false);
    }
  };

  const removeDelegate = async (delegationId) => {
    setDelegationLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/calendar/delegates/${delegationId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchDelegations();
      }
    } catch (error) {
      console.error('Failed to remove delegate:', error);
    } finally {
      setDelegationLoading(false);
    }
  };

  const updateDelegatePermissions = async (delegationId, permissions) => {
    setDelegationLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        can_view: permissions.can_view,
        can_edit: permissions.can_edit,
        can_create: permissions.can_create,
        can_delete: permissions.can_delete
      });

      const response = await fetch(getApiUrl(`/api/calendar/delegates/${delegationId}?${params}`), {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchDelegations();
      }
    } catch (error) {
      console.error('Failed to update delegate:', error);
    } finally {
      setDelegationLoading(false);
    }
  };

  const fetchEvents = async () => {
    // Only show loading if no cached data
    const cached = getCache('calendar_events');
    if (!cached?.data || cached.data.length === 0) {
      setLoading(true);
    }
    try {
      const token = localStorage.getItem('token');
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59, 999);

      let url = `api/calendar/events?start_date=${startOfMonth.toISOString()}&end_date=${endOfMonth.toISOString()}`;
      if (selectedUserId) {
        url += `&user_id=${selectedUserId}`;
      }

      const response = await fetch(getApiUrl(url), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEvents(data);
        setCache('calendar_events', data);
        detectConflicts(data);
        generateFocusSuggestions(data);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseDeadlines = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/virgo/cases?limit=100', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const cases = data.cases || data || [];
        // Extract deadlines from cases
        const deadlines = cases
          .filter(c => c.next_deadline || c.statute_of_limitations)
          .map(c => ({
            id: c.id,
            title: c.case_name || c.title,
            client: c.client_name,
            deadline: c.next_deadline || c.statute_of_limitations,
            type: c.next_deadline ? 'deadline' : 'sol',
            daysUntil: Math.ceil((new Date(c.next_deadline || c.statute_of_limitations) - new Date()) / (1000 * 60 * 60 * 24))
          }))
          .filter(d => d.daysUntil >= 0 && d.daysUntil <= 30)
          .sort((a, b) => a.daysUntil - b.daysUntil);
        setCaseDeadlines(deadlines);
        setCache('calendar_deadlines', deadlines);
      }
    } catch (error) {
      console.error('Failed to fetch case deadlines:', error);
    }
  };

  const detectConflicts = useCallback((eventList) => {
    const detected = [];
    for (let i = 0; i < eventList.length; i++) {
      for (let j = i + 1; j < eventList.length; j++) {
        const e1Start = new Date(eventList[i].start_time);
        const e1End = new Date(eventList[i].end_time);
        const e2Start = new Date(eventList[j].start_time);
        const e2End = new Date(eventList[j].end_time);

        if (e1Start < e2End && e2Start < e1End) {
          detected.push({
            event1: eventList[i],
            event2: eventList[j],
            date: e1Start
          });
        }
      }
    }
    setConflicts(detected);
  }, []);

  const generateFocusSuggestions = useCallback((eventList) => {
    const today = new Date();
    const todayEvents = eventList.filter(e => {
      const eventDate = new Date(e.start_time);
      return eventDate.toDateString() === today.toDateString();
    }).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

    const suggestions = [];

    // Morning focus time (before first meeting)
    if (todayEvents.length > 0) {
      const firstEvent = new Date(todayEvents[0].start_time);
      const morningStart = new Date(today);
      morningStart.setHours(8, 0, 0, 0);

      if (firstEvent.getHours() > 9) {
        suggestions.push({
          type: 'morning_focus',
          start: '8:00 AM',
          end: formatTime(todayEvents[0].start_time),
          duration: Math.round((firstEvent - morningStart) / (1000 * 60)),
          message: 'Deep work block before your first meeting'
        });
      }
    }

    // Gaps between meetings
    for (let i = 0; i < todayEvents.length - 1; i++) {
      const currentEnd = new Date(todayEvents[i].end_time);
      const nextStart = new Date(todayEvents[i + 1].start_time);
      const gapMinutes = (nextStart - currentEnd) / (1000 * 60);

      if (gapMinutes >= 60) {
        suggestions.push({
          type: 'gap_focus',
          start: formatTime(todayEvents[i].end_time),
          end: formatTime(todayEvents[i + 1].start_time),
          duration: Math.round(gapMinutes),
          message: `${Math.round(gapMinutes / 60)}h gap - perfect for focused work`
        });
      }
    }

    setFocusSuggestions(suggestions);
  }, []);

  const generateAIBriefing = useCallback(() => {
    const today = new Date();
    const todayEvents = events.filter(e => {
      const eventDate = new Date(e.start_time);
      return eventDate.toDateString() === today.toDateString();
    });

    const upcomingDeadlines = caseDeadlines.filter(d => d.daysUntil <= 7);
    const todayConflicts = conflicts.filter(c =>
      new Date(c.date).toDateString() === today.toDateString()
    );

    const hour = today.getHours();
    let greeting = 'Good evening';
    if (hour < 12) greeting = 'Good morning';
    else if (hour < 17) greeting = 'Good afternoon';

    let busyScore = 0;
    busyScore += todayEvents.length * 10;
    busyScore += upcomingDeadlines.length * 15;
    busyScore += todayConflicts.length * 20;

    let dayType = 'light';
    if (busyScore > 50) dayType = 'moderate';
    if (busyScore > 80) dayType = 'busy';
    if (busyScore > 100) dayType = 'intense';

    setAiBriefing({
      greeting,
      eventCount: todayEvents.length,
      nextEvent: todayEvents.length > 0 ? todayEvents.sort((a, b) =>
        new Date(a.start_time) - new Date(b.start_time)
      )[0] : null,
      conflictCount: todayConflicts.length,
      deadlineCount: upcomingDeadlines.length,
      dayType,
      focusTimeAvailable: focusSuggestions.reduce((acc, s) => acc + s.duration, 0),
      urgentDeadlines: upcomingDeadlines.filter(d => d.daysUntil <= 3)
    });
  }, [events, caseDeadlines, conflicts, focusSuggestions]);

  useEffect(() => {
    fetchCalendarUsers();
    fetchDelegations();
    fetchPendingAppointments();
  }, []);

  // Fetch firm event_type_colors on mount.
  // NOTE: /api/general (typed, reads the wide firm_settings row), NOT
  // /api/firm-settings/general -- that path hits the KEYED accessor, which
  // looks up firm_setting_kv and has no 'general' key. It returned 500 for
  // months (missing setting_key/setting_value columns) and now 404s. Either
  // way this fetch silently swallowed it and colours fell back to defaults.
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(getApiUrl('/api/general'), { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.event_type_colors) {
          setFirmColors(data.event_type_colors);
        }
      })
      .catch(() => {});
  }, []);


  useEffect(() => {
    fetchEvents();
    fetchCaseDeadlines();
  }, [currentDate, selectedUserId]);

  useEffect(() => {
    if (events.length >= 0) {
      generateAIBriefing();
    }
  }, [events, caseDeadlines, generateAIBriefing]);

  const getSelectedUserName = () => {
    if (!selectedUserId) return 'My Calendar';
    const user = calendarUsers.find(u => u.id === selectedUserId);
    if (!user) return 'My Calendar';
    return user.full_name || user.display_name || user.username;
  };


  // ─── Drag and Drop (Month View) ─────────────────────────────
  const [dragOverDate, setDragOverDate] = useState(null);

  const handleDragStart = (e, event) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({
      eventId: event.id,
      originalStart: event.start_time,
      originalEnd: event.end_time,
    }));
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDragOverDate(null);
  };

  const handleDragOver = (e, date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const dateStr = date.toDateString();
    if (dragOverDate !== dateStr) setDragOverDate(dateStr);
  };

  const handleDragLeave = () => {
    setDragOverDate(null);
  };

  const handleDrop = async (e, targetDate) => {
    e.preventDefault();
    setDragOverDate(null);
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      const origStart = new Date(data.originalStart);
      const origEnd = new Date(data.originalEnd);
      const duration = origEnd.getTime() - origStart.getTime();

      const newStart = new Date(targetDate);
      newStart.setHours(origStart.getHours(), origStart.getMinutes(), origStart.getSeconds());
      const newEnd = new Date(newStart.getTime() + duration);

      if (newStart.toDateString() === origStart.toDateString()) return; // same day, no-op

      // Optimistic update
      setEvents(prev => prev.map(ev =>
        ev.id === data.eventId
          ? { ...ev, start_time: newStart.toISOString(), end_time: newEnd.toISOString() }
          : ev
      ));

      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/calendar/events/${data.eventId}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          start_time: newStart.toISOString(),
          end_time: newEnd.toISOString(),
        }),
      });

      if (!response.ok) {
        // Revert on failure
        setEvents(prev => prev.map(ev =>
          ev.id === data.eventId
            ? { ...ev, start_time: data.originalStart, end_time: data.originalEnd }
            : ev
        ));
        console.error('Failed to move event');
      }
    } catch (err) {
      console.error('Drop failed:', err);
    }
  };


  // ─── Drag and Drop (Week View) ──────────────────────────────
  const handleWeekDragStart = (e, event) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({
      eventId: event.id,
      originalStart: event.start_time,
      originalEnd: event.end_time,
    }));
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleWeekDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDragOverDate(null);
  };

  const handleWeekDrop = async (e, targetDate, targetHour) => {
    e.preventDefault();
    setDragOverDate(null);
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      const origStart = new Date(data.originalStart);
      const origEnd = new Date(data.originalEnd);
      const duration = origEnd.getTime() - origStart.getTime();

      const newStart = new Date(targetDate);
      if (targetHour !== undefined) {
        newStart.setHours(targetHour, 0, 0, 0);
      } else {
        newStart.setHours(origStart.getHours(), origStart.getMinutes(), origStart.getSeconds());
      }
      const newEnd = new Date(newStart.getTime() + duration);

      if (newStart.getTime() === origStart.getTime()) return;

      setEvents(prev => prev.map(ev =>
        ev.id === data.eventId
          ? { ...ev, start_time: newStart.toISOString(), end_time: newEnd.toISOString() }
          : ev
      ));

      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/calendar/events/${data.eventId}`), {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ start_time: newStart.toISOString(), end_time: newEnd.toISOString() }),
      });

      if (!response.ok) {
        setEvents(prev => prev.map(ev =>
          ev.id === data.eventId
            ? { ...ev, start_time: data.originalStart, end_time: data.originalEnd }
            : ev
        ));
        console.error('Failed to move event');
      }
    } catch (err) { console.error('Week drop failed:', err); }
  };

  const goToPrevious = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (view === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    } else if (view === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() - 7);
      setCurrentDate(newDate);
    } else if (view === 'day') {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() - 1);
      setCurrentDate(newDate);
    }
  };

  const goToNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (view === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    } else if (view === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + 7);
      setCurrentDate(newDate);
    } else if (view === 'day') {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + 1);
      setCurrentDate(newDate);
    }
  };

  const goToToday = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // B4 (2026-08-22): Today = today's SCHEDULE, not just a cursor move. Forces
    // single-day view; paging then moves day-by-day (the view sticks), and the
    // month/week toggle remains the path back to month-at-today.
    setCurrentDate(new Date());
    setView('day');
  };

  useEffect(() => {
    if (view !== 'day') return;
    const d = currentDate.toISOString().slice(0, 10);
    api.get(`/calendar/computed-items?start_date=${d}&end_date=${d}`)
      .then((r) => setComputedItems(r.data || { deadlines: [], litigation_events: [] }))
      .catch(() => setComputedItems({ deadlines: [], litigation_events: [] }));
  }, [view, currentDate]);

  const handleViewChange = (e, newView) => {
    e.preventDefault();
    e.stopPropagation();
    setView(newView);
  };

  const getWeekDays = () => {
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - day);

    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      days.push(d);
    }
    return days;
  };

  const getHours = () => {
    const hours = [];
    for (let i = 6; i < 22; i++) {
      hours.push(i);
    }
    return hours;
  };

  const formatHour = (hour) => {
    if (hour === 0) return '12 AM';
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return '12 PM';
    return `${hour - 12} PM`;
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  const handleDateDoubleClick = (date) => {
    if (!date) return;
    setNewEventDate(date);
    setShowCreateModal(true);
  };

  const getEventsForDay = (date) => {
    if (!date) return [];
    return events.filter(event => {
      const eventDate = new Date(event.start_time);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const getDeadlinesForDay = (date) => {
    if (!date) return [];
    return caseDeadlines.filter(d => {
      const deadlineDate = new Date(d.deadline);
      return deadlineDate.toDateString() === date.toDateString();
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
  };

  const getDayTypeIcon = (type) => {
    switch (type) {
      case 'light': return <Sun className="w-5 h-5 text-green-400" />;
      case 'moderate': return <Sunrise className="w-5 h-5 text-yellow-400" />;
      case 'busy': return <Zap className="w-5 h-5 text-orange-400" />;
      case 'intense': return <AlertTriangle className="w-5 h-5 text-red-400" />;
      default: return <Sun className="w-5 h-5 text-green-400" />;
    }
  };

  const EventModal = ({ event, onClose, onEventUpdated, onEventDeleted }) => {
    // Pass C: compose-from-the-event. The note lands on the event's matter; if the
    // event has none, the matter is chosen EXPLICITLY here (resolve-and-remember
    // writes it back onto the event server-side, only-when-null). Titles are never
    // parsed for a matter.
    const [noteOpen, setNoteOpen] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [noteCaseId, setNoteCaseId] = useState('');
    const [noteCases, setNoteCases] = useState(null);
    const [noteBusy, setNoteBusy] = useState(false);
    const openNote = () => {
      setNoteOpen(true);
      if (!event.case_id && noteCases === null) {
        api.get('/cases', { params: { limit: 200 } })
          .then((r) => setNoteCases(Array.isArray(r.data) ? r.data : (r.data?.cases || [])))
          .catch(() => setNoteCases([]));
      }
    };
    const saveNote = async () => {
      const target = event.case_id || noteCaseId;
      if (!target) { alert('Pick the matter this note belongs to — it is never inferred.'); return; }
      if (!noteText.trim()) { alert('Write the note first.'); return; }
      setNoteBusy(true);
      try {
        await api.post(`/cases/${target}/updates`, {
          content: noteText, update_type: 'NOTE',
          visible_to_client: false, calendar_event_id: event.id,
        });
        setNoteOpen(false); setNoteText(''); setNoteCaseId('');
      } catch (e) {
        alert(e?.response?.data?.detail || 'Could not save the note.');
      } finally { setNoteBusy(false); }
    };
    const [isEditing, setIsEditing] = useState(false);

    // Helper to format date for datetime-local input (local time, not UTC)
    const formatForDatetimeLocal = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      // Format as YYYY-MM-DDTHH:mm in local timezone
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const EDIT_CATEGORIES = ['consultation', 'court', 'meeting', 'deadline', 'deposition', 'personal', 'focus', 'other'];
    // Map a stored recurrence_rule back to a simple UI option.
    const recurrenceOptionFromRule = (rule, isRecurring) => {
      if (!isRecurring || !rule) return 'none';
      const freq = (rule.frequency || rule.freq || '').toUpperCase();
      if (freq === 'DAILY') return 'daily';
      if (freq === 'WEEKLY') return 'weekly';
      if (freq === 'MONTHLY') return 'monthly';
      if (freq === 'YEARLY') return 'yearly';
      return 'none';
    };
    const isRecurringEvent = !!(event?.is_recurring || event?.recurrence_rule);
    const [editForm, setEditForm] = useState({
      title: event?.title || '',
      description: event?.description || '',
      location: event?.location || '',
      start_time: formatForDatetimeLocal(event?.start_time),
      end_time: formatForDatetimeLocal(event?.end_time),
      category: event?.category || 'other',
      all_day: event?.all_day || false,
      case_id: event?.case_id || '',
      recurrence: recurrenceOptionFromRule(event?.recurrence_rule, event?.is_recurring),
      recurrence_interval: event?.recurrence_rule?.interval || 1
    });
    const [editCases, setEditCases] = useState([]);

    useEffect(() => {
      const token = localStorage.getItem('token');
      fetch(getApiUrl('/api/virgo/cases?status=active'), { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setEditCases(Array.isArray(data) ? data : (data.cases || data.items || [])); })
        .catch(() => {});
    }, []);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteScope, setDeleteScope] = useState(null); // null | 'choosing'
    const [editScope, setEditScope] = useState(null); // null | 'choosing'

    // Attendee management
    const [attendees, setAttendees] = useState(event?.attendees || []);
    const [showAttendeeDropdown, setShowAttendeeDropdown] = useState(false);
    const [attendeeSearch, setAttendeeSearch] = useState('');
    const [externalEmail, setExternalEmail] = useState('');
    const [addingAttendee, setAddingAttendee] = useState(false);

    // Travel time
    const [travelInfo, setTravelInfo] = useState(null);
    const [travelLoading, setTravelLoading] = useState(false);

    useEffect(() => {
      if (event?.location && !String(event.location).startsWith('Phone:') && event?.id) {
        setTravelLoading(true);
        const token = localStorage.getItem('token');
        fetch(getApiUrl(`/api/calendar/events/${event.id}/travel-time`), {
          headers: { 'Authorization': `Bearer ${token}` }
        })
          .then(r => r.ok ? r.json() : null)
          .then(data => setTravelInfo(data))
          .catch(() => setTravelInfo(null))
          .finally(() => setTravelLoading(false));
      }
    }, [event?.id, event?.location]);

    if (!event) return null;

    // Filter users for attendee selection
    const filteredAttendees = calendarUsers.filter(user => {
      const searchTerm = attendeeSearch.toLowerCase();
      const isAlreadyAdded = attendees.some(a => a.user_id === user.id);
      const fullName = user.full_name?.toLowerCase() || '';
      const displayName = user.display_name?.toLowerCase() || '';
      const username = user.username?.toLowerCase() || '';
      const email = user.email?.toLowerCase() || '';

      return !isAlreadyAdded && (
        fullName.includes(searchTerm) ||
        displayName.includes(searchTerm) ||
        username.includes(searchTerm) ||
        email.includes(searchTerm)
      );
    });

    const addAttendee = async (attendeeData) => {
      setAddingAttendee(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(getApiUrl(`/api/calendar/events/${event.id}/attendees`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(attendeeData)
        });

        if (response.ok) {
          const newAttendee = await response.json();
          setAttendees([...attendees, newAttendee]);
          setAttendeeSearch('');
          setExternalEmail('');
          setShowAttendeeDropdown(false);
        } else {
          const error = await response.json();
          setSaveError(error.detail || 'Failed to add attendee');
        }
      } catch (error) {
        console.error('Failed to add attendee:', error);
        setSaveError('Failed to add attendee');
      } finally {
        setAddingAttendee(false);
      }
    };

    const addUserAttendee = (user) => {
      addAttendee({
        user_id: user.id,
        email: user.email,
        name: user.full_name || user.display_name || user.username,
        role: 'attendee',
        is_optional: false,
        notify: true
      });
    };


    const removeAttendee = async (attendee) => {
      if (!attendee.id) return;
      setAddingAttendee(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(getApiUrl(`/api/calendar/events/${event.id}/attendees/${attendee.id}`), {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          setAttendees(attendees.filter(a => a.id !== attendee.id));
        } else {
          const err = await response.json().catch(() => ({}));
          alert(err.detail || 'Failed to remove attendee');
        }
      } catch (e) {
        console.error('Failed to remove attendee:', e);
        alert('Failed to remove attendee');
      } finally {
        setAddingAttendee(false);
      }
    };

    // Master id + the occurrence slot for this instance (normal or already-modified).
    const seriesMasterId = event?.recurrence_parent_id || event?.id;
    const occurrenceStart = event?.original_start || event?.recurring_instance_date;

    const handleSave = async (scope) => {
      setSaveError(null);
      setEditScope(null);

      // Validate times
      const startDate = new Date(editForm.start_time);
      const endDate = new Date(editForm.end_time);

      if (endDate <= startDate) {
        setSaveError('End time must be after start time');
        return;
      }

      // Recurrence option -> is_recurring + recurrence_rule (whole-series scope).
      const dayNames = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
      const interval = parseInt(editForm.recurrence_interval, 10) || 1;
      let isRecurring = editForm.recurrence !== 'none';
      let recurrenceRule = null;
      switch (editForm.recurrence) {
        case 'daily':
          recurrenceRule = { frequency: 'DAILY', interval };
          break;
        case 'weekly':
          recurrenceRule = { frequency: 'WEEKLY', interval, byDay: [dayNames[startDate.getDay()]] };
          break;
        case 'monthly':
          recurrenceRule = { frequency: 'MONTHLY', interval, byMonthDay: startDate.getDate() };
          break;
        case 'yearly':
          recurrenceRule = { frequency: 'YEARLY', interval };
          break;
        default:
          isRecurring = false;
      }

      // Scope the write: 'single' edits one occurrence (exception child), else
      // the whole series. original_start (which occurrence) must be URL-encoded
      // because its "+" offset would otherwise decode to a space.
      let url = getApiUrl(`/api/calendar/events/${event.id}`);
      const body = {
        title: editForm.title,
        description: editForm.description,
        location: editForm.location,
        start_time: startDate.toISOString(),
        end_time: endDate.toISOString(),
        category: editForm.category,
        all_day: editForm.all_day,
        case_id: editForm.case_id || null,
      };
      if (scope === 'single') {
        url = getApiUrl(`/api/calendar/events/${seriesMasterId}?scope=single&original_start=${encodeURIComponent(occurrenceStart)}`);
      } else {
        if (scope === 'all') url = getApiUrl(`/api/calendar/events/${event.id}?scope=all`);
        // Recurrence rule is only meaningful for a whole-series edit.
        body.is_recurring = isRecurring;
        body.recurrence_rule = recurrenceRule;
      }

      setSaving(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });

        if (response.ok) {
          setIsEditing(false);
          setSaveError(null);
          if (onEventUpdated) onEventUpdated();
        } else {
          const error = await response.json();
          setSaveError(error.detail || 'Failed to update event');
        }
      } catch (error) {
        console.error('Failed to update event:', error);
        setSaveError('Failed to update event');
      } finally {
        setSaving(false);
      }
    };

    const handleDeleteWithScope = async (scope) => {
      setDeleting(true);
      try {
        const token = localStorage.getItem('token');
        let url = getApiUrl(`/api/calendar/events/${event.id}`);
        if (scope === 'single') {
          url = getApiUrl(`/api/calendar/events/${seriesMasterId}?scope=single&original_start=${encodeURIComponent(occurrenceStart)}`);
        } else if (scope) {
          url = getApiUrl(`/api/calendar/events/${event.id}?scope=${scope}`);
        }
        const response = await fetch(url, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          if (onEventDeleted) onEventDeleted();
          onClose();
        } else {
          let errorMsg = 'Failed to delete event';
          try {
            const error = await response.json();
            errorMsg = error.detail || errorMsg;
          } catch (e) {
            errorMsg = `Failed to delete event (Status: ${response.status})`;
          }
          console.error('Delete failed:', response.status, errorMsg);
          alert(errorMsg);
        }
      } catch (error) {
        console.error('Failed to delete event:', error);
        alert('Failed to delete event: ' + error.message);
      } finally {
        setDeleting(false);
        setShowDeleteConfirm(false);
        setDeleteScope(null);
      }
    };

    const handleDelete = () => handleDeleteWithScope(null);

    const handleDeleteClick = () => {
      if (event.is_recurring || event.recurrence_rule) {
        setDeleteScope('choosing');
      } else {
        setShowDeleteConfirm(true);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-cyan-500/30 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                  <CalendarIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      className="text-2xl font-bold text-white bg-slate-700/50 border border-cyan-500/30 rounded-lg px-3 py-1 w-full focus:outline-none focus:border-cyan-500"
                    />
                  ) : (
                    <h2 className="text-2xl font-bold text-white">{event.title}</h2>
                  )}
                  <div className="flex items-center gap-2">
                    <p className="text-cyan-300 text-sm">{isEditing ? 'Editing Event' : 'Event Details'}</p>
                    {!isEditing && event.category && (
                      <span className="text-xs px-2 py-0.5 rounded-full capitalize text-white" style={{ backgroundColor: getEventColor(event) }}>
                        {event.category}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg text-cyan-300 hover:text-white transition-colors">
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-start bg-slate-800/50 rounded-xl p-4">
                <Clock className="w-5 h-5 text-cyan-400 mr-3 mt-0.5" />
                {isEditing ? (
                  <div className="flex-1 space-y-2">
                    <div>
                      <label className="text-cyan-300 text-sm">Start</label>
                      <input
                        type="datetime-local"
                        value={editForm.start_time}
                        onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                        className="w-full bg-slate-700/50 border border-cyan-500/30 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="text-cyan-300 text-sm">End</label>
                      <input
                        type="datetime-local"
                        value={editForm.end_time}
                        onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })}
                        className="w-full bg-slate-700/50 border border-cyan-500/30 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-white font-medium">
                      {new Date(event.start_time).toLocaleDateString('en-US', {
                        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'America/New_York'
                      })}
                    </p>
                    <p className="text-cyan-300">
                      {formatTime(event.start_time)} - {formatTime(event.end_time)}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-start bg-slate-800/50 rounded-xl p-4">
                <MapPin className="w-5 h-5 text-cyan-400 mr-3 mt-0.5" />
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.location}
                    onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                    placeholder="Location"
                    className="flex-1 bg-slate-700/50 border border-cyan-500/30 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-cyan-500"
                  />
                ) : event.location && event.location.startsWith('Phone:') ? (
                  <a href={`tel:${event.location.replace(/[^0-9+]/g, '')}`}
                     className="text-cyan-100 font-semibold text-lg hover:text-cyan-300">
                    {event.location.replace(/^Phone:\s*/, '').replace(/^(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3')}
                    <span className="ml-2 text-xs font-normal text-cyan-400">client callback number</span>
                  </a>
                ) : (
                  <p className="text-cyan-200">{event.location || 'No location'}</p>
                )}
              </div>


              {/* Travel Time (not for phone-number pseudo-locations) */}
              {!isEditing && event.location && !event.location.startsWith('Phone:') && (
                travelLoading ? (
                  <div className="flex items-center gap-2 bg-slate-800/50 rounded-xl p-3">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-cyan-500 border-t-transparent" />
                    <span className="text-cyan-300 text-sm">Calculating travel time...</span>
                  </div>
                ) : travelInfo && (travelInfo.drive_time_minutes || travelInfo.distance) ? (
                  <div className="bg-gradient-to-r from-green-600/10 to-emerald-600/10 rounded-xl p-4 border border-green-500/30">
                    <div className="flex items-center gap-2 text-green-300 text-sm font-medium">
                      <span>🚗</span>
                      <span>{travelInfo.drive_time_minutes} min drive</span>
                      {travelInfo.distance && <span className="text-green-400/80">({travelInfo.distance})</span>}
                    </div>
                    <a
                      href={`https://maps.apple.com/?daddr=${encodeURIComponent(event.location)}&dirflg=d`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-400 hover:text-cyan-300 text-xs mt-1 inline-block hover:underline"
                    >
                      Open in Apple Maps →
                    </a>
                  </div>
                ) : null
              )}
              <div className="flex items-start bg-slate-800/50 rounded-xl p-4">
                <FileText className="w-5 h-5 text-cyan-400 mr-3 mt-0.5" />
                {isEditing ? (
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    placeholder="Description"
                    rows={3}
                    className="flex-1 bg-slate-700/50 border border-cyan-500/30 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-cyan-500 resize-none"
                  />
                ) : (
                  <p className="text-cyan-200">{event.description || 'No description'}</p>
                )}
              </div>

              {/* Event type / all-day / case — editable to match the create form */}
              {isEditing && (
                <div className="bg-slate-800/50 rounded-xl p-4 space-y-3">
                  <div>
                    <label className="text-cyan-300 text-sm">Event type</label>
                    <select
                      value={editForm.category}
                      onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                      className="w-full bg-slate-700/50 border border-cyan-500/30 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-cyan-500 capitalize"
                    >
                      {EDIT_CATEGORIES.map(c => (
                        <option key={c} value={c} className="capitalize">{c}</option>
                      ))}
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-cyan-200 text-sm">
                    <input
                      type="checkbox"
                      checked={editForm.all_day}
                      onChange={(e) => setEditForm({ ...editForm, all_day: e.target.checked })}
                    />
                    All-day event
                  </label>
                  <div>
                    <label className="text-cyan-300 text-sm">Matter / case</label>
                    <select
                      value={editForm.case_id}
                      onChange={(e) => setEditForm({ ...editForm, case_id: e.target.value })}
                      className="w-full bg-slate-700/50 border border-cyan-500/30 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-cyan-500"
                    >
                      <option value="">No matter</option>
                      {editCases.map(c => (
                        <option key={c.id} value={c.id}>{c.case_number ? `${c.case_number} — ` : ''}{c.title || c.name || c.case_name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-cyan-300 text-sm">Repeat</label>
                    <div className="flex gap-2">
                      <select
                        value={editForm.recurrence}
                        onChange={(e) => setEditForm({ ...editForm, recurrence: e.target.value })}
                        className="flex-1 bg-slate-700/50 border border-cyan-500/30 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-cyan-500"
                      >
                        <option value="none">Does not repeat</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                      {editForm.recurrence !== 'none' && (
                        <input
                          type="number"
                          min="1"
                          value={editForm.recurrence_interval}
                          onChange={(e) => setEditForm({ ...editForm, recurrence_interval: e.target.value })}
                          className="w-20 bg-slate-700/50 border border-cyan-500/30 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-cyan-500"
                          title="Interval"
                        />
                      )}
                    </div>
                    {isRecurringEvent && (
                      <p className="text-xs text-amber-300/80 mt-1">
                        Changes apply to all events in the series. Editing a single occurrence is coming soon.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Attendees Section - Always visible, editable when in edit mode */}
              <div className="bg-slate-800/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <Users className="w-5 h-5 text-cyan-400 mr-3" />
                    <p className="text-cyan-200 font-medium">
                      {attendees.length} attendee{attendees.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  {addingAttendee && (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-cyan-500 border-t-transparent" />
                  )}
                </div>

                {/* Current Attendees */}
                {attendees.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {attendees.map((attendee, idx) => (
                      <div key={attendee.id || idx} className="flex items-center justify-between text-sm bg-slate-700/30 rounded-lg px-3 py-2">
                        <div className="flex items-center">
                          <span className={`w-2 h-2 rounded-full mr-2 ${
                            attendee.rsvp_status === 'accepted' ? 'bg-green-500' :
                            attendee.rsvp_status === 'declined' ? 'bg-red-500' :
                            attendee.rsvp_status === 'tentative' ? 'bg-yellow-500' : 'bg-gray-400'
                          }`} />
                          <span className="text-cyan-200">
                            {attendee.name || attendee.email}
                            {attendee.role === 'organizer' && ' (Organizer)'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-cyan-400 capitalize">
                            {attendee.rsvp_status || 'pending'}
                          </span>
                          {isEditing && attendee.role !== 'organizer' && (
                            <button
                              type="button"
                              onClick={() => removeAttendee(attendee)}
                              title="Remove attendee (sends a cancellation)"
                              className="text-red-300 hover:text-red-200 text-xs px-1"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Attendees - Always visible */}
                <div className="border-t border-cyan-500/20 pt-4">
                  <p className="text-xs font-medium text-cyan-400 mb-2 uppercase tracking-wide">Add Attendees</p>

                  {/* Search Team Members */}
                  <div className="relative mb-2">
                    <input
                      type="text"
                      value={attendeeSearch}
                      onChange={(e) => {
                        setAttendeeSearch(e.target.value);
                        setShowAttendeeDropdown(true);
                      }}
                      onFocus={() => setShowAttendeeDropdown(true)}
                      className="w-full px-4 py-2 bg-slate-700/50 border border-cyan-500/30 text-white rounded-lg focus:outline-none focus:border-cyan-500 placeholder-cyan-400/50 text-sm"
                      placeholder="Search team members..."
                    />
                    <UserPlus className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400" />

                    {showAttendeeDropdown && filteredAttendees.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-slate-800 border border-cyan-500/30 rounded-lg shadow-xl max-h-40 overflow-y-auto">
                        {filteredAttendees.slice(0, 5).map(user => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => addUserAttendee(user)}
                            className="w-full px-4 py-2 text-left hover:bg-cyan-500/20 transition-all flex items-center justify-between"
                          >
                            <div>
                              <span className="text-sm text-white">{user.full_name || user.display_name || user.username}</span>
                              <span className="text-xs text-cyan-400 ml-2">({user.email})</span>
                            </div>
                            <Mail className="w-3 h-3 text-cyan-400" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Add client/contact by name (tenant-scoped picker; raw email only as external fallback) */}
                  <ContactPicker
                    placeholder="Search contacts by name, email, or phone…"
                    allowRawEmail
                    excludeEmails={attendees.map(a => a.email).filter(Boolean)}
                    onSelect={(person) => addAttendee({
                      user_id: null,
                      email: person.email,
                      name: person.name,
                      role: 'attendee',
                      is_optional: false,
                      notify: true
                    })}
                  />

                  <p className="text-xs text-cyan-400 mt-2 flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    Invitations will be sent via email
                  </p>
                </div>
              </div>

              {!isEditing && event.visage_meeting_link && (
                <div className="flex items-center bg-gradient-to-r from-cyan-600/20 to-blue-600/20 rounded-xl p-4 border border-cyan-500/30">
                  <Video className="w-5 h-5 text-cyan-400 mr-3" />
                  <a
                    href={event.visage_meeting_link}
                    className="text-cyan-300 hover:text-white hover:underline font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Join Video Meeting
                  </a>
                </div>
              )}

              {!isEditing && event.case_id && (
                <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 rounded-xl p-4 border border-cyan-500/30">
                  <div className="flex items-center">
                    <Scale className="w-5 h-5 text-cyan-400 mr-3" />
                    <p className="text-sm text-cyan-200">
                      Linked to case: <span className="font-medium text-white">{event.case_id}</span>
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Delete Confirmation */}
            {showDeleteConfirm && (
              <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                <p className="text-red-300 mb-3">Are you sure you want to delete this event? This action cannot be undone.</p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-4 py-2 text-cyan-200 bg-slate-700/50 rounded-xl hover:bg-slate-700 border border-slate-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="px-4 py-2 text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors flex items-center gap-2"
                  >
                    {deleting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Delete Event
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}


            {/* Recurring Event Edit Scope */}
            {editScope === 'choosing' && (
              <div className="mt-4 p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-xl">
                <p className="text-cyan-200 mb-3 text-sm font-medium">This is a recurring event. Apply your changes to:</p>
                <div className="space-y-2">
                  {[['single', 'This event only', false], ['future', 'This and following events', true], ['all', 'All events in series', false]].map(([scope, label, disabled]) => (
                    <button
                      key={scope}
                      onClick={() => handleSave(scope)}
                      disabled={saving || disabled}
                      title={disabled ? 'Coming soon — edit this event or the whole series for now' : ''}
                      className="w-full text-left px-4 py-2.5 text-sm text-cyan-200 bg-slate-700/50 rounded-lg hover:bg-cyan-500/20 border border-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {label}{disabled ? ' (coming soon)' : ''}
                    </button>
                  ))}
                  <button
                    onClick={() => setEditScope(null)}
                    className="w-full text-center px-4 py-2 text-sm text-cyan-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Recurring Event Delete Scope */}
            {deleteScope === 'choosing' && (
              <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                <p className="text-red-300 mb-3 text-sm font-medium">This is a recurring event. What would you like to delete?</p>
                <div className="space-y-2">
                  {[['single', 'This event only', false], ['future', 'This and following events', true], ['all', 'All events in series', false]].map(([scope, label, disabled]) => (
                    <button
                      key={scope}
                      onClick={() => handleDeleteWithScope(scope)}
                      disabled={deleting || disabled}
                      title={disabled ? 'Coming soon — delete the whole series for now' : ''}
                      className="w-full text-left px-4 py-2.5 text-sm text-cyan-200 bg-slate-700/50 rounded-lg hover:bg-red-500/20 border border-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {label}{disabled ? ' (coming soon)' : ''}
                    </button>
                  ))}
                  <button
                    onClick={() => setDeleteScope(null)}
                    className="w-full text-center px-4 py-2 text-sm text-cyan-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {/* Save Error Display */}
            {saveError && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                <p className="text-red-300 text-sm">{saveError}</p>
              </div>
            )}

            {noteOpen && (
              <div className="mt-4 p-3 bg-slate-800/60 border border-cyan-500/30 rounded-xl space-y-2">
                {!event.case_id && (
                  <select value={noteCaseId} onChange={(e) => setNoteCaseId(e.target.value)}
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm">
                    <option value="">— pick the matter (required, never inferred) —</option>
                    {(noteCases || []).map((c) => (
                      <option key={c.id} value={c.id}>{c.case_name || c.id}</option>
                    ))}
                  </select>
                )}
                <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3}
                          placeholder="Hearing note — internal by default"
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm" />
                <div className="flex justify-end gap-2">
                  <button onClick={() => setNoteOpen(false)} className="px-3 py-1.5 text-cyan-200 text-sm">Cancel</button>
                  <button onClick={saveNote} disabled={noteBusy}
                          className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm">
                    {noteBusy ? 'Saving…' : 'Save note'}
                  </button>
                </div>
              </div>
            )}
            <div className="mt-6 flex justify-between">
              <button
                onClick={handleDeleteClick}
                className="px-4 py-2 text-red-300 bg-red-500/10 rounded-xl hover:bg-red-500/20 border border-red-500/30 transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
              <div className="flex space-x-3">
                {isEditing ? (
                  <>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 text-cyan-200 bg-slate-700/50 rounded-xl hover:bg-slate-700 border border-slate-600 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => { if (isRecurringEvent) { setEditScope('choosing'); } else { handleSave(); } }}
                      disabled={saving}
                      className="px-4 py-2 text-white bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl hover:from-green-700 hover:to-emerald-700 transition-colors flex items-center gap-2"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={onClose} className="px-4 py-2 text-cyan-200 bg-slate-700/50 rounded-xl hover:bg-slate-700 border border-slate-600 transition-colors">
                      Close
                    </button>
                    <button onClick={openNote}
                            className="px-4 py-2 text-white bg-gradient-to-r from-amber-600 to-orange-600 rounded-xl hover:from-amber-700 hover:to-orange-700 transition-colors">
                      Add Note
                    </button>
                    <button
                      onClick={() => { setIsEditing(true); setSaveError(null); }}
                      className="px-4 py-2 text-white bg-gradient-to-r from-cyan-600 to-blue-600 rounded-xl hover:from-cyan-700 hover:to-blue-700 transition-colors flex items-center gap-2"
                    >
                      <Edit3 className="w-4 h-4" />
                      Edit Event
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const days = getDaysInMonth();
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-slate-900 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center">
                <CalendarIcon className="w-7 h-7 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full flex items-center justify-center">
                <Sparkles className="w-3 h-3 text-white" />
              </div>
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Calendar
              </h1>
              <p className="text-cyan-300 text-sm">AI-Powered Scheduling</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* User Calendar Selector */}
            <div className="relative">
              <button
                onClick={() => setShowUserDropdown(!showUserDropdown)}
                className="flex items-center gap-2 px-4 py-2.5 bg-slate-800/50 border border-cyan-500/30 rounded-xl hover:bg-slate-700/50 transition-colors"
              >
                <Users className="w-5 h-5 text-cyan-400" />
                <span className="text-white font-medium">{getSelectedUserName()}</span>
                <ChevronDown className={`w-4 h-4 text-cyan-400 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showUserDropdown && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-slate-800 border border-cyan-500/30 rounded-xl shadow-2xl z-50 overflow-hidden">
                  <div className="p-2">
                    <button
                      onClick={() => {
                        setSelectedUserId(null);
                        setShowUserDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                        !selectedUserId ? 'bg-cyan-500/20 text-white' : 'text-cyan-200 hover:bg-slate-700'
                      }`}
                    >
                      My Calendar
                    </button>
                    <div className="border-t border-cyan-500/20 my-2"></div>
                    <p className="px-3 py-1 text-xs text-cyan-400 font-medium">Staff Calendars</p>
                    {calendarUsers.map(user => (
                      <button
                        key={user.id}
                        onClick={() => {
                          setSelectedUserId(user.id);
                          setShowUserDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                          selectedUserId === user.id ? 'bg-cyan-500/20 text-white' : 'text-cyan-200 hover:bg-slate-700'
                        }`}
                      >
                        <span className="font-medium">{user.full_name || user.display_name || user.username}</span>
                        <span className="text-cyan-400 text-xs ml-2">({user.role})</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setShowDelegationModal(true)}
              className="flex items-center px-4 py-2.5 bg-slate-700/50 border border-cyan-500/30 text-cyan-200 rounded-xl hover:bg-cyan-500/20 transition-all"
              title="Calendar Delegation"
            >
              <Share2 className="w-5 h-5 mr-2" />
              Delegate
            </button>
            <button
              onClick={() => setShowFocusTimeModal(true)}
              className="flex items-center px-4 py-2.5 bg-gradient-to-r from-green-600/80 to-emerald-600/80 border border-green-500/30 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg shadow-green-500/20"
              title="Create recurring focus time"
            >
              <Target className="w-5 h-5 mr-2" />
              Focus Time
            </button>
            {LEGAL_INTEL_ON_CALENDAR && (
            <button
              onClick={() => setShowLegalIntel(true)}
              className="flex items-center px-4 py-2.5 bg-slate-700/50 border border-cyan-500/30 text-cyan-200 rounded-xl hover:bg-cyan-500/20 transition-all"
              title="Legal Intel"
            >
              <Scale className="w-5 h-5 mr-2" />
              Legal Intel
            </button>
            )}
            <button
              onClick={() => setProposeApptOpen(true)}
              className="flex items-center px-4 py-2.5 bg-slate-700/50 border border-cyan-500/30 text-cyan-200 rounded-xl hover:bg-cyan-500/20 transition-all"
              title="Propose an appointment to a person (they respond via a public link)"
            >
              <CalendarIcon className="w-5 h-5 mr-2" />
              Propose Appointment
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg shadow-cyan-500/30"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Event
            </button>
          </div>
        </div>
      </div>

      {/* Staff-initiated appointment requests (pending) */}
      <div className="max-w-7xl mx-auto">
        <PendingRequestsPanel reloadKey={proposeReload} />
      </div>
      <ProposeAppointmentModal isOpen={proposeApptOpen} onClose={() => setProposeApptOpen(false)} onCreated={() => setProposeReload((n) => n + 1)} />

      {/* AI Briefing Panel */}
      {aiBriefing && (
        <div className="max-w-7xl mx-auto mb-6">
          <div
            className="bg-gradient-to-r from-slate-800/80 to-slate-900/30 backdrop-blur-sm border border-cyan-500/30 rounded-2xl overflow-hidden cursor-pointer"
            onClick={() => setShowBriefing(!showBriefing)}
          >
            <div className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                  <Brain className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    {aiBriefing.greeting}
                    {getDayTypeIcon(aiBriefing.dayType)}
                  </h3>
                  <p className="text-cyan-300 text-sm">
                    {aiBriefing.eventCount} events today • {aiBriefing.conflictCount > 0 ? `${aiBriefing.conflictCount} conflicts` : 'No conflicts'} • {Math.round(aiBriefing.focusTimeAvailable / 60)}h focus time available
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {aiBriefing.urgentDeadlines.length > 0 && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 border border-red-500/30 rounded-full">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <span className="text-red-300 text-sm font-medium">{aiBriefing.urgentDeadlines.length} urgent deadline{aiBriefing.urgentDeadlines.length > 1 ? 's' : ''}</span>
                  </div>
                )}
                {showBriefing ? <ChevronUp className="w-5 h-5 text-cyan-400" /> : <ChevronDown className="w-5 h-5 text-cyan-400" />}
              </div>
            </div>

            {showBriefing && (
              <div className="px-6 pb-6 grid grid-cols-4 gap-4">
                {/* Next Event */}
                {aiBriefing.nextEvent && (
                  <div className="bg-slate-800/50 rounded-xl p-4 border border-cyan-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Play className="w-4 h-4 text-green-400" />
                      <span className="text-sm font-medium text-cyan-300">Next Up</span>
                    </div>
                    <p className="text-white font-medium truncate">{aiBriefing.nextEvent.title}</p>
                    <p className="text-cyan-400 text-sm">{formatTime(aiBriefing.nextEvent.start_time)}</p>
                  </div>
                )}

                {/* Focus Time */}
                {focusSuggestions.length > 0 && (
                  <div className="bg-slate-800/50 rounded-xl p-4 border border-cyan-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4 text-cyan-400" />
                      <span className="text-sm font-medium text-cyan-300">Focus Time</span>
                    </div>
                    <p className="text-white font-medium">{Math.round(aiBriefing.focusTimeAvailable / 60)}h available</p>
                    <p className="text-cyan-400 text-sm">{focusSuggestions.length} block{focusSuggestions.length > 1 ? 's' : ''} identified</p>
                  </div>
                )}

                {/* Case Deadlines */}
                <div className="bg-slate-800/50 rounded-xl p-4 border border-cyan-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Scale className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm font-medium text-cyan-300">Case Deadlines</span>
                  </div>
                  <p className="text-white font-medium">{caseDeadlines.length} this month</p>
                  <p className="text-cyan-400 text-sm">{aiBriefing.deadlineCount} within 7 days</p>
                </div>

                {/* Conflicts */}
                <div className={`rounded-xl p-4 border ${conflicts.length > 0 ? 'bg-red-500/10 border-red-500/30' : 'bg-slate-800/50 border-cyan-500/20'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className={`w-4 h-4 ${conflicts.length > 0 ? 'text-red-400' : 'text-green-400'}`} />
                    <span className="text-sm font-medium text-cyan-300">Conflicts</span>
                  </div>
                  <p className={`font-medium ${conflicts.length > 0 ? 'text-red-300' : 'text-green-300'}`}>
                    {conflicts.length > 0 ? `${conflicts.length} detected` : 'All clear'}
                  </p>
                  <p className="text-cyan-400 text-sm">
                    {conflicts.length > 0 ? 'Review needed' : 'No overlapping events'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pending Appointments Panel */}
      {pendingAppointments.length > 0 && (
        <div className="max-w-7xl mx-auto mb-6">
          <div className="bg-gradient-to-r from-amber-900/30 to-slate-900/30 backdrop-blur-sm border border-amber-500/30 rounded-2xl overflow-hidden">
            <div className="px-6 py-4 flex items-center justify-between cursor-pointer" onClick={() => setShowPendingPanel(!showPendingPanel)}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    Pending Appointment Requests
                    <span className="px-2 py-0.5 bg-amber-500/30 text-amber-300 text-sm rounded-full">{pendingAppointments.length}</span>
                  </h3>
                  <p className="text-amber-300/70 text-sm">Client-requested appointments awaiting your approval</p>
                </div>
              </div>
              {showPendingPanel ? <ChevronUp className="w-5 h-5 text-amber-400" /> : <ChevronDown className="w-5 h-5 text-amber-400" />}
            </div>

            {showPendingPanel && (
              <div className="px-6 pb-6 space-y-3">
                {pendingAppointments.map(appt => (
                  <div key={appt.id} className="bg-slate-800/60 rounded-xl p-4 border border-amber-500/20">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="text-white font-semibold">{appt.client_name}</span>
                          {appt.case_name && <span className="text-xs px-2 py-0.5 bg-cyan-500/20 text-cyan-300 rounded-full">{appt.case_name}</span>}
                          {appt.urgency !== 'routine' && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${appt.urgency === 'urgent' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`}>
                              {appt.urgency === 'urgent' ? 'Urgent' : 'Time-Sensitive'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-cyan-300">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {appt.local_date && appt.local_time
                              ? `${appt.local_date} at ${appt.local_time}${appt.timezone_label ? ` ${appt.timezone_label}` : ''}`
                              : `${new Date(appt.start_time).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} at ${new Date(appt.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                          </span>
                          <span className="flex items-center gap-1">
                            {appt.meeting_type === 'video' ? <Video className="w-3.5 h-3.5" /> : appt.meeting_type === 'phone' ? <Bell className="w-3.5 h-3.5" /> : <MapPin className="w-3.5 h-3.5" />}
                            {appt.meeting_type === 'in_person' ? 'In-Person' : appt.meeting_type === 'video' ? 'Video Call' : 'Phone Call'}
                          </span>
                        </div>
                        {appt.meeting_purpose && <p className="text-cyan-400/70 text-sm mt-1">{appt.meeting_purpose}</p>}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {rejectingId !== appt.id && (
                          <>
                            <button
                              onClick={() => handleApproveAppointment(appt.id)}
                              disabled={approving === appt.id}
                              className="flex items-center gap-1 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg text-sm font-medium hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 transition-all"
                            >
                              <Check className="w-4 h-4" /> Approve
                            </button>
                            <button
                              onClick={() => setRejectingId(appt.id)}
                              className="flex items-center gap-1 px-4 py-2 bg-red-500/20 text-red-300 border border-red-500/30 rounded-lg text-sm font-medium hover:bg-red-500/30 transition-all"
                            >
                              <X className="w-4 h-4" /> Reject
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {rejectingId === appt.id && (
                      <div className="mt-3 pt-3 border-t border-amber-500/20 flex items-center gap-2">
                        <input
                          type="text"
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleRejectAppointment(appt.id, rejectReason, false); if (e.key === 'Escape') { setRejectingId(null); setRejectReason(''); } }}
                          placeholder="Reason for rejection (optional)..."
                          autoFocus
                          className="flex-1 px-3 py-2 bg-slate-700/50 border border-red-500/30 rounded-lg text-white text-sm placeholder-slate-400 focus:ring-2 focus:ring-red-500/50 focus:outline-none"
                        />
                        <span className="text-[11px] text-slate-400 whitespace-nowrap" title="Enter declines without placing a call">
                          &#8629; declines without calling
                        </span>
                        <button
                          onClick={() => handleRejectAppointment(appt.id, rejectReason, false)}
                          disabled={approving === appt.id}
                          title="Decline this request. No call is placed."
                          className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition-all whitespace-nowrap"
                        >
                          Decline <span className="ml-1 opacity-60 font-normal">&#8629;</span>
                        </button>
                        <span className="mx-1 h-6 w-px bg-slate-600" aria-hidden="true" />
                        <button
                          onClick={() => handleRejectAppointment(appt.id, rejectReason, true)}
                          disabled={approving === appt.id}
                          title="Decline, and have Astraea phone this client with alternative times."
                          className="flex items-center gap-1.5 px-3 py-2 bg-transparent text-amber-300 border border-amber-500/50 rounded-lg text-sm font-normal hover:bg-amber-500/10 disabled:opacity-50 transition-all whitespace-nowrap"
                        >
                          <Phone className="w-3.5 h-3.5" /> Decline &amp; call with other times
                        </button>
                        <button
                          onClick={() => { setRejectingId(null); setRejectReason(''); }}
                          className="px-3 py-2 text-slate-400 hover:text-white text-sm transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Navigation Bar */}
      <div className="max-w-7xl mx-auto bg-slate-800/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button type="button" onClick={goToPrevious} className="p-2 hover:bg-cyan-500/20 rounded-xl text-cyan-300 transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold text-white min-w-[280px] text-center">
              {view === 'month' && `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
              {view === 'week' && (() => {
                const weekDays = getWeekDays();
                const startDate = weekDays[0];
                const endDate = weekDays[6];
                if (startDate.getMonth() === endDate.getMonth()) {
                  return `${monthNames[startDate.getMonth()]} ${startDate.getDate()} - ${endDate.getDate()}, ${startDate.getFullYear()}`;
                }
                return `${monthNames[startDate.getMonth()]} ${startDate.getDate()} - ${monthNames[endDate.getMonth()]} ${endDate.getDate()}, ${endDate.getFullYear()}`;
              })()}
              {view === 'day' && currentDate.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' })}
            </h2>
            <button type="button" onClick={goToNext} className="p-2 hover:bg-cyan-500/20 rounded-xl text-cyan-300 transition-colors">
              <ChevronRight className="w-5 h-5" />
            </button>
            <button type="button" onClick={goToToday} className="px-4 py-1.5 text-sm bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-200 rounded-xl transition-colors border border-cyan-500/30">
              Today
            </button>
          </div>

          <div className="flex space-x-2 bg-slate-800/50 p-1 rounded-xl">
            {['month', 'week', 'day'].map((v) => (
              <button
                key={v}
                type="button"
                onClick={(e) => handleViewChange(e, v)}
                className={`px-4 py-1.5 rounded-lg transition-all capitalize ${
                  view === v
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg'
                    : 'text-cyan-300 hover:text-white hover:bg-cyan-500/20'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Month View */}
      {view === 'month' && (
        <div className="max-w-7xl mx-auto bg-slate-800/30 backdrop-blur-sm border border-cyan-500/20 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-7 border-b border-cyan-500/20">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="p-4 text-center text-sm font-semibold text-cyan-300 bg-slate-800/50">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {days.map((date, index) => {
              const dayEvents = date ? getEventsForDay(date) : [];
              const dayDeadlines = date ? getDeadlinesForDay(date) : [];
              const isToday = date && date.toDateString() === new Date().toDateString();
              const hasConflict = conflicts.some(c =>
                date && new Date(c.date).toDateString() === date.toDateString()
              );

              return (
                <div
                  key={index}
                  onDoubleClick={() => handleDateDoubleClick(date)}
                  onDragOver={date ? (e) => handleDragOver(e, date) : undefined}
                  onDragLeave={date ? handleDragLeave : undefined}
                  onDrop={date ? (e) => handleDrop(e, date) : undefined}
                  className={`min-h-32 p-2 border-r border-b border-cyan-500/10 transition-colors ${
                    !date ? 'bg-slate-900/50' : 'hover:bg-cyan-500/5 cursor-pointer'
                  } ${isToday ? 'bg-cyan-500/10' : ''} ${hasConflict ? 'ring-1 ring-red-500/50 ring-inset' : ''} ${date && dragOverDate === date.toDateString() ? 'bg-cyan-500/20 ring-2 ring-cyan-400/50 ring-inset' : ''}`}
                >
                  {date && (
                    <>
                      <div className={`text-sm font-medium mb-2 flex items-center justify-between ${
                        isToday ? 'text-cyan-300' : 'text-white'
                      }`}>
                        <span className={isToday ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold' : ''}>
                          {date.getDate()}
                        </span>
                        {hasConflict && <AlertTriangle className="w-3 h-3 text-red-400" />}
                      </div>

                      {/* Deadlines */}
                      {dayDeadlines.slice(0, 1).map(deadline => (
                        <div
                          key={deadline.id}
                          className="text-xs p-1.5 rounded-lg mb-1 cursor-pointer bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30"
                        >
                          <div className="text-yellow-300 truncate font-medium flex items-center gap-1">
                            <Scale className="w-3 h-3" />
                            {deadline.title}
                          </div>
                        </div>
                      ))}

                      {/* Events */}
                      <div className="space-y-1">
                        {dayEvents.slice(0, 2).map(event => (
                          <div
                            key={event.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, event)}
                            onDragEnd={handleDragEnd}
                            onClick={() => {
                              setSelectedEvent(event);
                              setShowEventModal(true);
                            }}
                            className="text-xs p-1.5 rounded-lg cursor-grab hover:scale-[1.02] transition-transform active:cursor-grabbing"
                            style={{
                              background: `linear-gradient(135deg, ${getEventColor(event)}dd, ${getEventColor(event)}cc)`
                            }}
                          >
                            <div className="text-white truncate font-medium flex items-center gap-1">
                              <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: getEventColor(event), flexShrink: 0, display: 'inline-block', border: '1px solid rgba(255,255,255,0.4)' }} />
                              {event.title}
                            </div>
                            <div className="text-white/80">{formatTime(event.start_time)}</div>
                          </div>
                        ))}
                        {(dayEvents.length + dayDeadlines.length) > 3 && (
                          <div className="text-xs text-cyan-400 pl-1 font-medium">
                            +{dayEvents.length + dayDeadlines.length - 3} more
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Week View */}
      {view === 'week' && (() => {
        const hours = getHours();
        const hourHeight = 60; // pixels per hour
        const startHour = hours[0]; // First hour shown (6 AM)

        // Calculate event position and height
        const getEventStyle = (event) => {
          const start = new Date(event.start_time);
          const end = new Date(event.end_time);
          const startMinutes = start.getHours() * 60 + start.getMinutes();
          const endMinutes = end.getHours() * 60 + end.getMinutes();
          const duration = endMinutes - startMinutes;

          const top = ((start.getHours() - startHour) * 60 + start.getMinutes()) * (hourHeight / 60);
          const height = Math.max(duration * (hourHeight / 60), 24); // Minimum 24px height

          return { top: `${top}px`, height: `${height}px` };
        };

        return (
          <div className="max-w-7xl mx-auto bg-slate-800/30 backdrop-blur-sm border border-cyan-500/20 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-8 border-b border-cyan-500/20">
              <div className="p-4 text-center text-sm font-semibold text-cyan-300 bg-slate-800/50"></div>
              {getWeekDays().map(date => {
                const isToday = date.toDateString() === new Date().toDateString();
                return (
                  <div key={date.toISOString()} className={`p-4 text-center ${isToday ? 'bg-cyan-500/30' : 'bg-slate-800/50'}`}>
                    <div className={`text-sm font-semibold ${isToday ? 'text-cyan-200' : 'text-cyan-400'}`}>
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                    </div>
                    <div className={`text-lg font-bold ${isToday ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white w-8 h-8 rounded-full flex items-center justify-center mx-auto' : 'text-cyan-200'}`}>
                      {date.getDate()}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="max-h-[600px] overflow-y-auto">
              <div className="grid grid-cols-8">
                {/* Time column */}
                <div className="bg-slate-800/30">
                  {hours.map(hour => (
                    <div key={hour} className="h-[60px] p-2 text-xs text-cyan-400 text-right pr-4 border-b border-cyan-500/10">
                      {formatHour(hour)}
                    </div>
                  ))}
                </div>

                {/* Day columns with positioned events */}
                {getWeekDays().map(date => {
                  const dayEvents = getEventsForDay(date);
                  const isToday = date.toDateString() === new Date().toDateString();
                  return (
                    <div
                      key={date.toISOString()}
                      className={`relative border-l border-cyan-500/10 ${isToday ? 'bg-cyan-500/10' : ''}`}
                      onDoubleClick={() => handleDateDoubleClick(date)}
                      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                      onDrop={(e) => handleWeekDrop(e, date)}
                    >
                      {/* Hour grid lines */}
                      {hours.map(hour => (
                        <div key={hour} className="h-[60px] border-b border-cyan-500/10 hover:bg-cyan-500/5 transition-colors cursor-pointer" onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }} onDrop={(e) => { e.stopPropagation(); handleWeekDrop(e, date, hour); }} />
                      ))}

                      {/* Positioned events */}
                      {dayEvents.map(event => {
                        const style = getEventStyle(event);
                        return (
                          <div
                            key={event.id}
                            draggable
                            onDragStart={(e) => handleWeekDragStart(e, event)}
                            onDragEnd={handleWeekDragEnd}
                            onClick={() => {
                              setSelectedEvent(event);
                              setShowEventModal(true);
                            }}
                            className="absolute left-1 right-1 rounded-lg cursor-grab active:cursor-grabbing hover:scale-[1.02] transition-transform overflow-hidden z-10"
                            style={{
                              ...style,
                              background: `linear-gradient(135deg, ${getEventColor(event)}dd, ${getEventColor(event)}cc)`
                            }}
                          >
                            <div className="p-1.5 h-full">
                              <div className="text-white truncate font-medium text-xs flex items-center gap-1">
                                <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: getEventColor(event), flexShrink: 0, display: 'inline-block', border: '1px solid rgba(255,255,255,0.4)' }} />
                                {event.title}
                              </div>
                              <div className="text-white/80 text-xs">{formatTime(event.start_time)}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Day View */}
      {view === 'day' && (computedItems.deadlines.length + computedItems.litigation_events.length) > 0 && (
        <div className="mb-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <div className="text-amber-300 text-xs font-semibold uppercase tracking-wide mb-1">
            Deadlines & litigation — due this day
          </div>
          {computedItems.deadlines.map((d) => (
            <div key={`cd-${d.id}`} className="text-sm text-amber-100">
              ⚑ {d.deadline_type || 'Deadline'} · {d.case_name || d.case_id} {d.description ? `— ${d.description}` : ''}
            </div>
          ))}
          {computedItems.litigation_events.map((l) => (
            <div key={`le-${l.id}`} className="text-sm text-amber-100">
              ⚖️ {l.event_type || 'Litigation event'} · {l.case_name || l.case_id} {l.description ? `— ${l.description}` : ''}
            </div>
          ))}
        </div>
      )}
      {view === 'day' && (() => {
        const hours = getHours();
        const hourHeight = 60; // pixels per hour
        const startHour = hours[0]; // First hour shown (6 AM)
        const dayEvents = getEventsForDay(currentDate);

        // Calculate event position and height
        const getEventStyle = (event) => {
          const start = new Date(event.start_time);
          const end = new Date(event.end_time);
          const startMinutes = start.getHours() * 60 + start.getMinutes();
          const endMinutes = end.getHours() * 60 + end.getMinutes();
          const duration = endMinutes - startMinutes;

          const top = ((start.getHours() - startHour) * 60 + start.getMinutes()) * (hourHeight / 60);
          const height = Math.max(duration * (hourHeight / 60), 30); // Minimum 30px height

          return { top: `${top}px`, height: `${height}px` };
        };

        return (
          <div className="max-w-7xl mx-auto bg-slate-800/30 backdrop-blur-sm border border-cyan-500/20 rounded-2xl overflow-hidden">
            <div className={`p-4 text-center text-lg font-semibold text-white border-b border-cyan-500/20 ${currentDate.toDateString() === new Date().toDateString() ? 'bg-cyan-500/30' : 'bg-slate-800/50'}`}>
              {currentDate.toDateString() === new Date().toDateString() && (
                <span className="inline-block bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-xs px-2 py-0.5 rounded-full mr-2">Today</span>
              )}
              {currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' })}
            </div>

            <div className="max-h-[600px] overflow-y-auto">
              <div className="grid grid-cols-12">
                {/* Time column */}
                <div className="col-span-1 bg-slate-800/30">
                  {hours.map(hour => {
                    const isFocusTime = focusSuggestions.some(s => {
                      const sStart = parseInt(s.start.split(':')[0]);
                      const sEnd = parseInt(s.end.split(':')[0]);
                      return hour >= sStart && hour < sEnd;
                    });
                    return (
                      <div key={hour} className="h-[60px] p-2 text-xs text-cyan-400 text-right pr-4 border-b border-cyan-500/10 flex items-start justify-end gap-1">
                        {isFocusTime && <Target className="w-3 h-3 text-green-400" />}
                        {formatHour(hour)}
                      </div>
                    );
                  })}
                </div>

                {/* Events column */}
                <div
                  className="col-span-11 relative cursor-pointer"
                  onDoubleClick={() => handleDateDoubleClick(currentDate)}
                >
                  {/* Hour grid lines with focus time highlighting */}
                  {hours.map(hour => {
                    const isFocusTime = focusSuggestions.some(s => {
                      const sStart = parseInt(s.start.split(':')[0]);
                      const sEnd = parseInt(s.end.split(':')[0]);
                      return hour >= sStart && hour < sEnd;
                    });
                    return (
                      <div key={hour} className={`h-[60px] border-b border-cyan-500/10 ${isFocusTime ? 'bg-green-500/5' : ''} hover:bg-cyan-500/5 transition-colors`} />
                    );
                  })}

                  {/* Positioned events */}
                  {dayEvents.map(event => {
                    const style = getEventStyle(event);
                    return (
                      <div
                        key={event.id}
                        onClick={() => {
                          setSelectedEvent(event);
                          setShowEventModal(true);
                        }}
                        className="absolute left-2 right-2 rounded-xl cursor-pointer hover:scale-[1.01] transition-transform overflow-hidden z-10"
                        style={{
                          ...style,
                          background: `linear-gradient(135deg, ${getEventColor(event)}dd, ${getEventColor(event)}cc)`
                        }}
                      >
                        <div className="p-3 h-full">
                          <div className="text-white font-medium flex items-center gap-1.5">
                            <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: getEventColor(event), flexShrink: 0, display: 'inline-block', border: '1px solid rgba(255,255,255,0.4)' }} />
                            {event.title}
                          </div>
                          <div className="text-white/80 text-sm">
                            {formatTime(event.start_time)} - {formatTime(event.end_time)}
                          </div>
                          {event.location && (
                            <div className="text-white/70 text-sm flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" />
                              {event.location}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Sidebar - Case Deadlines */}
      {caseDeadlines.length > 0 && (
        <div className="fixed right-6 top-1/2 -translate-y-1/2 w-72 bg-slate-800/90 backdrop-blur-sm border border-cyan-500/30 rounded-2xl p-4 shadow-2xl max-h-[60vh] overflow-y-auto">
          <div className="flex items-center gap-2 mb-4">
            <Scale className="w-5 h-5 text-yellow-400" />
            <h3 className="text-white font-semibold">Upcoming Deadlines</h3>
          </div>
          <div className="space-y-2">
            {caseDeadlines.slice(0, 8).map(deadline => (
              <div
                key={deadline.id}
                className={`p-3 rounded-xl border ${
                  deadline.daysUntil <= 3
                    ? 'bg-red-500/10 border-red-500/30'
                    : deadline.daysUntil <= 7
                      ? 'bg-yellow-500/10 border-yellow-500/30'
                      : 'bg-slate-700/50 border-cyan-500/20'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-bold ${
                    deadline.daysUntil <= 3 ? 'text-red-400' :
                    deadline.daysUntil <= 7 ? 'text-yellow-400' : 'text-cyan-400'
                  }`}>
                    {deadline.daysUntil === 0 ? 'TODAY' :
                     deadline.daysUntil === 1 ? 'TOMORROW' :
                     `${deadline.daysUntil} DAYS`}
                  </span>
                  <span className="text-xs text-cyan-400">
                    {new Date(deadline.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
                <p className="text-white text-sm font-medium truncate">{deadline.title}</p>
                <p className="text-cyan-400 text-xs truncate">{deadline.client}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-slate-800/90 backdrop-blur-sm border border-cyan-500/30 shadow-2xl rounded-xl px-6 py-3">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-400"></div>
            <span className="text-cyan-200">Loading events...</span>
          </div>
        </div>
      )}

      <CreateEventModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setNewEventDate(null);
        }}
        onEventCreated={(event) => {
          fetchEvents();
          setShowCreateModal(false);
          setNewEventDate(null);
        }}
        calendarUsers={calendarUsers}
        defaultOwnerId={selectedUserId}
        defaultDate={newEventDate}
      />

      {/* Focus Time Modal - pre-configured for recurring focus blocks */}
      <CreateEventModal
        isOpen={showFocusTimeModal}
        onClose={() => setShowFocusTimeModal(false)}
        onEventCreated={(event) => {
          fetchEvents();
          setShowFocusTimeModal(false);
        }}
        calendarUsers={calendarUsers}
        defaultOwnerId={selectedUserId}
        defaultCategory="focus"
        defaultRecurrence="weekdays"
        defaultTitle="Focus Time"
      />

      {showEventModal && (
        <EventModal
          event={selectedEvent}
          onClose={() => {
            setShowEventModal(false);
            setSelectedEvent(null);
          }}
          onEventUpdated={() => {
            fetchEvents();
            setShowEventModal(false);
            setSelectedEvent(null);
          }}
          onEventDeleted={() => {
            fetchEvents();
          }}
        />
      )}


      {/* Legal Intel Panel */}
      {showLegalIntel && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1" onClick={() => setShowLegalIntel(false)} />
          <div className="w-96 bg-gradient-to-b from-slate-800 to-slate-900 border-l border-cyan-500/30 shadow-2xl flex flex-col animate-slide-in-right">
            <style>{'@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } } .animate-slide-in-right { animation: slideInRight 0.2s ease-out; }'}</style>
            <div className="p-4 border-b border-cyan-500/20 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Scale className="w-5 h-5 text-cyan-400" />
                <span className="text-lg font-semibold text-white">Legal Intel</span>
                <span className="text-xs px-2 py-0.5 bg-cyan-500/20 text-cyan-300 rounded-full">INTEL</span>
              </div>
              <button onClick={() => setShowLegalIntel(false)} className="p-1 hover:bg-slate-700 rounded-lg text-cyan-300">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex border-b border-cyan-500/20">
              {[['health','Health'],['chains','Deadlines'],['dark','Dark'],['time','Time'],['comms','Comms']].map(([id,label]) => (
                <button key={id} onClick={() => setLegalIntelTab(id)} className={`flex-1 py-2 text-xs font-semibold transition-colors ${legalIntelTab === id ? 'text-cyan-300 border-b-2 border-cyan-400 bg-slate-800/80' : 'text-cyan-400/60 hover:text-cyan-300'}`}>
                  {label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {legalLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-cyan-500 border-t-transparent" />
                </div>
              ) : legalIntelTab === 'health' ? (
                legalHealth ? (
                  <div>
                    <div className="text-center py-6">
                      <div className="relative w-24 h-24 mx-auto">
                        <svg viewBox="0 0 36 36" className="w-24 h-24" style={{ transform: 'rotate(-90deg)' }}>
                          <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#1e293b" strokeWidth="3" />
                          <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none"
                            stroke={legalHealth.score >= 75 ? '#22c55e' : legalHealth.score >= 50 ? '#eab308' : '#ef4444'}
                            strokeWidth="3" strokeDasharray={`${legalHealth.score || 0}, 100`} strokeLinecap="round" />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-2xl font-bold" style={{ color: legalHealth.score >= 75 ? '#22c55e' : legalHealth.score >= 50 ? '#eab308' : '#ef4444' }}>{legalHealth.score || 0}</span>
                        </div>
                      </div>
                      <p className="mt-2 font-semibold" style={{ color: legalHealth.score >= 90 ? '#22c55e' : legalHealth.score >= 75 ? '#84cc16' : legalHealth.score >= 60 ? '#eab308' : legalHealth.score >= 40 ? '#f97316' : '#ef4444' }}>
                        {legalHealth.score >= 90 ? 'Excellent' : legalHealth.score >= 75 ? 'Good' : legalHealth.score >= 60 ? 'Fair' : legalHealth.score >= 40 ? 'Needs Attention' : 'Critical'}
                      </p>
                    </div>
                    {legalHealth.issues?.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="text-xs font-semibold text-cyan-400 uppercase">Issues</p>
                        {legalHealth.issues.map((issue, i) => (
                          <div key={i} className="p-3 bg-slate-800/60 rounded-xl border border-cyan-500/20">
                            <p className="text-sm text-white">{issue.title || issue.message || issue}</p>
                            {issue.severity && <span className={`text-xs ${issue.severity === 'high' ? 'text-red-400' : 'text-amber-400'} capitalize`}>{issue.severity}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : <p className="text-center text-cyan-400/60 py-12">No health data available.</p>
              ) : legalIntelTab === 'chains' ? (
                legalChains.length === 0 ? <p className="text-center text-cyan-400/60 py-12">No pending deadline chains.</p> : (
                  <div className="space-y-3">
                    {legalChains.map(chain => (
                      <div key={chain.id} className="p-3 bg-slate-800/60 rounded-xl border border-cyan-500/20">
                        <p className="text-sm font-medium text-white">{chain.case_name || chain.title}</p>
                        <p className="text-xs text-cyan-400">Trigger: {chain.trigger_event || '—'}</p>
                        <p className="text-xs text-cyan-400">Jurisdiction: {chain.jurisdiction || '—'}</p>
                        <p className="text-xs text-cyan-400 mb-2">Items: {chain.item_count || chain.items?.length || 0}</p>
                        <div className="flex gap-2">
                          <button onClick={() => handleChainAction(chain.id, 'approve')} className="flex-1 py-1.5 text-xs font-semibold bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700">Approve</button>
                          <button onClick={() => handleChainAction(chain.id, 'reject')} className="flex-1 py-1.5 text-xs text-cyan-300 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/10">Reject</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : legalIntelTab === 'dark' ? (
                <div className="space-y-3">
                  {legalDarkPeriods.length === 0 && <p className="text-center text-cyan-400/60 py-8">No unavailable periods.</p>}
                  {legalDarkPeriods.map(p => (
                    <div key={p.id} className="p-3 bg-slate-800/60 rounded-xl border border-cyan-500/20 flex justify-between items-center">
                      <div>
                        <p className="text-sm text-white">{p.label || p.title}</p>
                        <p className="text-xs text-cyan-400">{p.start_date ? new Date(p.start_date).toLocaleDateString() : '—'} – {p.end_date ? new Date(p.end_date).toLocaleDateString() : '—'}</p>
                        <p className="text-xs text-cyan-300 capitalize">{p.type || 'unavailable'}</p>
                      </div>
                      <button onClick={() => handleDeletePeriod(p.id)} className="p-1 text-red-400 hover:bg-red-500/20 rounded"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              ) : legalIntelTab === 'time' ? (
                legalTimeSuggestions.length === 0 ? <p className="text-center text-cyan-400/60 py-12">No unbilled time suggestions.</p> : (
                  <div className="space-y-3">
                    {legalTimeSuggestions.map(item => (
                      <div key={item.id} className="p-3 bg-slate-800/60 rounded-xl border border-cyan-500/20">
                        <p className="text-sm text-white mb-1">{item.description}</p>
                        <div className="flex gap-3 text-xs text-cyan-400 mb-2">
                          <span>{item.duration_minutes || item.duration} min</span>
                          {(item.suggested_billing_code || item.billing_code) && <span className="text-cyan-300">{item.suggested_billing_code || item.billing_code}</span>}
                        </div>
                        <button className="px-3 py-1 text-xs font-semibold bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg hover:from-cyan-700 hover:to-blue-700">Create Entry</button>
                      </div>
                    ))}
                  </div>
                )
              ) : legalIntelTab === 'comms' ? (
                legalComms.length === 0 ? <p className="text-center text-cyan-400/60 py-12">No pending communications.</p> : (
                  <div className="space-y-3">
                    {legalComms.map(comm => (
                      <div key={comm.id} className="p-3 bg-slate-800/60 rounded-xl border border-cyan-500/20">
                        <p className="text-sm font-medium text-white">{comm.subject || comm.title}</p>
                        <p className="text-xs text-cyan-400">{comm.client_name || comm.recipient}</p>
                        {comm.event_date && <p className="text-xs text-cyan-300 mb-2">{new Date(comm.event_date).toLocaleDateString()}</p>}
                        <div className="flex gap-2">
                          <button onClick={() => handleCommAction(comm.id, 'approve')} className="flex-1 py-1.5 text-xs font-semibold bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg">Approve</button>
                          <button onClick={() => handleCommAction(comm.id, 'dismiss')} className="flex-1 py-1.5 text-xs text-cyan-300 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/10">Dismiss</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Calendar Delegation Modal */}
      {showDelegationModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-cyan-500/30 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-cyan-500/20">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                    <Share2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white">Calendar Delegation</h2>
                    <p className="text-sm text-cyan-300">Share calendar access with team members</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDelegationModal(false)}
                  className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-cyan-300" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* People I've Delegated To */}
              <div className="mb-8">
                <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-cyan-400" />
                  People Who Can Access My Calendar
                </h3>

                {myDelegates.length === 0 ? (
                  <div className="text-center py-6 bg-slate-800/50 rounded-xl border border-cyan-500/10">
                    <p className="text-cyan-300">No delegates yet. Add someone below.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {myDelegates.map((delegate) => (
                      <div key={delegate.id} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-cyan-500/20">
                        <div>
                          <p className="text-white font-medium">{delegate.full_name || delegate.display_name || delegate.username}</p>
                          <p className="text-sm text-cyan-400">{delegate.email}</p>
                          <div className="flex gap-2 mt-2">
                            {delegate.can_view && <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded-full flex items-center gap-1"><Eye className="w-3 h-3" /> View</span>}
                            {delegate.can_edit && <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-300 rounded-full flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</span>}
                            {delegate.can_create && <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-300 rounded-full flex items-center gap-1"><PlusCircle className="w-3 h-3" /> Create</span>}
                            {delegate.can_delete && <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-300 rounded-full flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</span>}
                          </div>
                        </div>
                        <button
                          onClick={() => removeDelegate(delegate.id)}
                          disabled={delegationLoading}
                          className="px-3 py-2 text-red-300 hover:text-red-200 hover:bg-red-500/20 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Delegate */}
                <div className="mt-4">
                  <p className="text-sm font-medium text-cyan-300 mb-2">Add a delegate:</p>
                  <div className="flex flex-wrap gap-2">
                    {calendarUsers
                      .filter(user => !myDelegates.some(d => d.delegate_id === user.id))
                      .slice(0, 6)
                      .map(user => (
                        <button
                          key={user.id}
                          onClick={() => addDelegate(user.id, { can_view: true, can_edit: true, can_create: true, can_delete: false })}
                          disabled={delegationLoading}
                          className="px-3 py-2 bg-slate-700/50 border border-cyan-500/30 rounded-lg text-sm text-cyan-200 hover:bg-cyan-500/20 transition-colors flex items-center gap-2 disabled:opacity-50"
                        >
                          <UserPlus className="w-4 h-4" />
                          {user.full_name || user.display_name || user.username}
                        </button>
                      ))}
                  </div>
                </div>
              </div>

              {/* Calendars Delegated To Me */}
              <div>
                <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-cyan-400" />
                  Calendars I Can Access
                </h3>

                {delegatedToMe.length === 0 ? (
                  <div className="text-center py-6 bg-slate-800/50 rounded-xl border border-cyan-500/10">
                    <p className="text-cyan-300">No one has delegated their calendar to you yet.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {delegatedToMe.map((delegation) => (
                      <div key={delegation.id} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-cyan-500/20">
                        <div>
                          <p className="text-white font-medium">{delegation.full_name || delegation.display_name || delegation.username}</p>
                          <p className="text-sm text-cyan-400">{delegation.email}</p>
                          <div className="flex gap-2 mt-2">
                            {delegation.can_view && <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded-full flex items-center gap-1"><Eye className="w-3 h-3" /> View</span>}
                            {delegation.can_edit && <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-300 rounded-full flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</span>}
                            {delegation.can_create && <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-300 rounded-full flex items-center gap-1"><PlusCircle className="w-3 h-3" /> Create</span>}
                            {delegation.can_delete && <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-300 rounded-full flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</span>}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedUserId(delegation.owner_id);
                            setShowDelegationModal(false);
                          }}
                          className="px-4 py-2 bg-cyan-500/20 border border-cyan-500/30 text-cyan-200 rounded-lg hover:bg-cyan-500/30 transition-colors flex items-center gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          View Calendar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-cyan-500/20 flex justify-end">
              <button
                onClick={() => setShowDelegationModal(false)}
                className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl hover:from-cyan-600 hover:to-blue-600 transition-all"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
